import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { MdCloudOff } from "react-icons/md"
import { RiErrorWarningLine } from "react-icons/ri"
import { useProcessActions } from "~/hooks/useProcessActions"
import { CloudImage } from "../../CloudProviderTypes"
import CloudImageBrowserErrorScreen from "../../common/browser/CloudImageBrowserErrorScreen"
import { useCloudProviderActions } from "../../hooks/useCloudProviderActions"
import { useCloudProviderContext } from "../../hooks/useCloudProviderContext"
import { useVNZImageActions } from "../hooks/useVNZImageActions"
import { useVNZImages } from "../hooks/useVNZImages"
import VNZImageBrowserDesktop from "./VNZImageBrowserDesktop"
import VNZImageBrowserMobile from "./VNZImageBrowserMobile"

interface Props {
  isMobile: boolean
}

const VNZImageBrowser: React.FC<Props> = ({ isMobile }) => {
  const { t } = useTranslation()
  const { startFetchingVNZCloudImages: startFetchingVnzCloudImages } =
    useProcessActions()
  const { setIsImportingImages } = useCloudProviderContext()
  const { loadVNZFullImage } = useVNZImageActions()
  const { isLoading, hasLoadedImages, allVnzImages, hasError, hasNoImages } =
    useVNZImages()

  const allImages: CloudImage[] = allVnzImages.map(vnzImage => {
    return {
      id: vnzImage.id,
      provider: "verizon-cloud",
      url: vnzImage.fullImageLocalUrl ?? "",
      thumbnailUrl: vnzImage.thumbnailLocalUrl ?? "",
    }
  })

  useEffect(() => {
    if (!hasLoadedImages) {
      startFetchingVnzCloudImages()
    }
  }, [hasLoadedImages, startFetchingVnzCloudImages])

  const {
    selectedImageIds,
    hasSelectedImages,
    isImportingImages,
    handleImageSelect,
    handleUploadImages,
    closeCloudProviderModal,
  } = useCloudProviderActions()

  const BATCH_SIZE = 3

  const handleUploadImageEnhanced = async () => {
    setIsImportingImages(true)
    try {
      const selectedImages = allVnzImages.filter(vnzImage =>
        selectedImageIds.includes(vnzImage.id)
      )

      const selectedCloudImages: CloudImage[] = []

      for (let i = 0; i < selectedImages.length; i += BATCH_SIZE) {
        const batch = selectedImages.slice(i, i + BATCH_SIZE)
        const batchResults = await Promise.all(
          batch.map(async image => {
            const fullImageUrl = await loadVNZFullImage(image)
            return {
              id: image.id,
              provider: "verizon-cloud" as const,
              url: fullImageUrl,
              thumbnailUrl: image.thumbnailLocalUrl ?? "",
            }
          })
        )
        selectedCloudImages.push(...batchResults)
      }

      await handleUploadImages(selectedCloudImages)
    } catch (error) {
      console.error("Error uploading images from Verizon Cloud:", error)
      setIsImportingImages(false)
    }
  }

  if (hasError) {
    return (
      <CloudImageBrowserErrorScreen
        icon={RiErrorWarningLine}
        title={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.ErrorScreen.Title"
        )}
        description={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.ErrorScreen.Description"
        )}
        onRetry={startFetchingVnzCloudImages}
      />
    )
  }

  if (hasNoImages) {
    return (
      <CloudImageBrowserErrorScreen
        icon={MdCloudOff}
        title={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.NoImagesScreen.Title"
        )}
        description={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.NoImagesScreen.Description"
        )}
        onRetry={startFetchingVnzCloudImages}
      />
    )
  }

  if (isMobile) {
    return (
      <VNZImageBrowserMobile
        isLoading={isLoading}
        allImages={allImages}
        handleUploadImages={handleUploadImageEnhanced}
        hasSelectedImages={hasSelectedImages}
        isImportingImages={isImportingImages}
        handleImageSelect={handleImageSelect}
        selectedImageIds={selectedImageIds}
      />
    )
  }

  return (
    <VNZImageBrowserDesktop
      isLoading={isLoading}
      allImages={allImages}
      closeCloudProviderModal={closeCloudProviderModal}
      handleUploadImages={handleUploadImageEnhanced}
      hasSelectedImages={hasSelectedImages}
      isImportingImages={isImportingImages}
      handleImageSelect={handleImageSelect}
      selectedImageIds={selectedImageIds}
    />
  )
}

export default VNZImageBrowser
